/**
 * @lodex-pro/web-admin/features/operators/hooks — Task 25.3 (DEC-114).
 *
 * TanStack Query hooks over the PLATFORM_ADMIN operator-management API
 * (/v1/platform/operators): list/search, detail, paginated wells, edit
 * (display name + abbreviation), and manual add. The merge (with its preview) and the duplicate
 * review are in dedupe-hooks.ts.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts, type EmptyBody } from '@lodex-pro/web-shared';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface OperatorSummary {
  readonly id: string;
  readonly name: string;
  readonly displayName: string | null;
  readonly abbreviation: string | null;
  readonly regulatorOperatorId: string | null;
  readonly state: string | null;
  readonly isProducer: boolean;
  readonly isDisposal: boolean;
  readonly isActive: boolean;
  readonly mergedIntoId: string | null;
  /** The surviving row a merged-away operator now points to (null when live). */
  readonly mergedInto: { readonly id: string; readonly name: string } | null;
  /** Every state it operates in: its own, its wells', and every row merged into it. */
  readonly states: readonly string[];
  readonly wellCount: number;
  readonly haulerCount: number;
  readonly hasDuplicates: boolean;
}

export interface OperatorHauler {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly abbreviation: string;
  readonly isActive: boolean;
}

export interface DuplicateOperator {
  readonly id: string;
  readonly name: string;
  readonly abbreviation: string | null;
  readonly state: string | null;
  readonly haulerCount: number;
  readonly wellCount: number;
  /** The group's suggested survivor (the merge dialog pre-selects it as KEEP). */
  readonly isSuggestedSurvivor: boolean;
  /** Its verdict against the survivor; null for the survivor itself. */
  readonly verdict: { readonly kind: string; readonly text: string } | null;
}

export interface OperatorWell {
  readonly id: string;
  readonly wellName: string;
  readonly apiNumber: string;
  /** Two-letter state code (wells.state is NOT NULL). */
  readonly state: string;
  readonly county: string | null;
  readonly wellType: string | null;
}

export interface WellsPage {
  readonly items: readonly OperatorWell[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface OperatorDetail {
  readonly operator: OperatorSummary;
  readonly haulers: readonly OperatorHauler[];
  readonly possibleDuplicates: readonly DuplicateOperator[];
  /** The duplicates merged into this operator, through any chain (empty when none). */
  readonly mergedFrom: readonly { readonly id: string; readonly name: string }[];
  /** This row's own verdict in its duplicate group (null when it is the survivor or alone). */
  readonly duplicateVerdict: {
    readonly kind: string;
    readonly text: string;
    readonly notes: readonly string[];
  } | null;
  readonly wells: WellsPage;
}

export interface OperatorFilters {
  readonly q?: string;
  readonly state?: string;
  readonly isProducer?: boolean;
  readonly isDisposal?: boolean;
  readonly isActive?: boolean;
  readonly hasAbbreviation?: boolean;
  readonly hasDuplicates?: boolean;
  /** ONLY the merged-away rows (hidden by default). */
  readonly mergedOnly?: boolean;
  readonly sort?: 'name' | 'haulerCount' | 'wellCount';
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const OPERATORS_KEY = ['platform', 'operators'] as const;
export const OPERATOR_KEY = (id: string): readonly string[] => ['platform', 'operators', id];
export const OPERATOR_WELLS_KEY = (
  id: string,
  page: number,
  q = '',
): readonly (string | number)[] => ['platform', 'operators', id, 'wells', q, page];

// ─── Queries ─────────────────────────────────────────────────────────────────

function toParams(f: OperatorFilters): string {
  const p = new URLSearchParams();
  if (f.q !== undefined && f.q !== '') p.set('q', f.q);
  if (f.state !== undefined && f.state !== '') p.set('state', f.state);
  if (f.isProducer !== undefined) p.set('isProducer', String(f.isProducer));
  if (f.isDisposal !== undefined) p.set('isDisposal', String(f.isDisposal));
  if (f.isActive !== undefined) p.set('isActive', String(f.isActive));
  if (f.hasAbbreviation !== undefined) p.set('hasAbbreviation', String(f.hasAbbreviation));
  if (f.hasDuplicates !== undefined) p.set('hasDuplicates', String(f.hasDuplicates));
  if (f.mergedOnly === true) p.set('mergedOnly', 'true');
  p.set('sort', f.sort ?? 'name');
  return p.toString();
}

/**
 * The operator list. A caller that puts typed text into `filters.q` debounces it first
 * (useDebouncedValue, OPEN-194): every request reads the whole catalog's duplicate groups.
 * `enabled: false` keeps the query dormant (a picker with nothing typed yet).
 */
export function useOperators(
  filters: OperatorFilters,
  enabled = true,
): ReturnType<typeof useApiQuery<{ operators: readonly OperatorSummary[] }>> {
  const opts = useApiOpts();
  const params = toParams(filters);
  return useApiQuery<{ operators: readonly OperatorSummary[] }>(
    opts,
    `/v1/platform/operators?${params}`,
    { queryKey: [...OPERATORS_KEY, params], staleTime: 15_000, enabled },
  );
}

export function useOperator(id: string): ReturnType<typeof useApiQuery<OperatorDetail>> {
  const opts = useApiOpts();
  return useApiQuery<OperatorDetail>(opts, `/v1/platform/operators/${id}`, {
    queryKey: OPERATOR_KEY(id),
    staleTime: 15_000,
  });
}

/** One page of the operator's wells; `q` searches well name or API number on the server. */
export function useOperatorWells(
  id: string,
  page: number,
  enabled: boolean,
  q = '',
): ReturnType<typeof useApiQuery<WellsPage>> {
  const opts = useApiOpts();
  const params = new URLSearchParams({ page: String(page) });
  if (q !== '') params.set('q', q);
  return useApiQuery<WellsPage>(opts, `/v1/platform/operators/${id}/wells?${params.toString()}`, {
    queryKey: OPERATOR_WELLS_KEY(id, page, q),
    enabled,
    staleTime: 15_000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export interface UpdateOperatorBody {
  displayName?: string | null;
  abbreviation?: string | null;
}

export function useUpdateOperator(
  id: string,
): ReturnType<typeof useApiMutation<UpdateOperatorBody, { operator: OperatorSummary }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateOperatorBody, { operator: OperatorSummary }>(
    opts,
    `/v1/platform/operators/${id}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: OPERATOR_KEY(id) });
        void qc.invalidateQueries({ queryKey: OPERATORS_KEY });
      },
    },
  );
}

export interface MergeResult {
  merged: boolean;
  wellsMoved: number;
  haulersMoved: number;
  haulersDeleted: number;
}

function useOperatorActive(
  id: string,
  action: 'deactivate' | 'reactivate',
): ReturnType<typeof useApiMutation<EmptyBody, { operator: { id: string; isActive: boolean } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<EmptyBody, { operator: { id: string; isActive: boolean } }>(
    opts,
    `/v1/platform/operators/${id}/${action}`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: OPERATOR_KEY(id) });
        void qc.invalidateQueries({ queryKey: OPERATORS_KEY });
      },
    },
  );
}

export function useDeactivateOperator(
  id: string,
): ReturnType<typeof useApiMutation<EmptyBody, { operator: { id: string; isActive: boolean } }>> {
  return useOperatorActive(id, 'deactivate');
}

export function useReactivateOperator(
  id: string,
): ReturnType<typeof useApiMutation<EmptyBody, { operator: { id: string; isActive: boolean } }>> {
  return useOperatorActive(id, 'reactivate');
}

export interface CreateOperatorBody {
  name: string;
  state?: string;
  isProducer?: boolean;
  isDisposal?: boolean;
  abbreviation?: string;
}

export function useCreateOperator(
  onDone: (id: string) => void,
): ReturnType<typeof useApiMutation<CreateOperatorBody, { operator: OperatorSummary }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateOperatorBody, { operator: OperatorSummary }>(
    opts,
    '/v1/platform/operators',
    'POST',
    {
      onSuccess: (res) => {
        void qc.invalidateQueries({ queryKey: OPERATORS_KEY });
        onDone(res.operator.id);
      },
    },
  );
}
